import {
  Badge,
  Card,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type { SavedPaymentMethod } from '@/lib/stripeBillingService';

export interface CardOnFileSectionProps {
  paymentMethods?: SavedPaymentMethod[];
  loading?: boolean;
}

function brandIcon(brand: string): string {
  const b = brand.toLowerCase();
  if (b === 'visa') return 'Visa';
  if (b === 'mastercard') return 'Mastercard';
  if (b === 'amex' || b === 'american_express') return 'Amex';
  if (b === 'discover') return 'Discover';
  return brand || 'Card';
}

export function CardOnFileSection({
  paymentMethods,
  loading,
}: CardOnFileSectionProps): JSX.Element {
  const defaultCard = paymentMethods?.find((pm) => pm.isDefault) ?? paymentMethods?.[0];

  return (
    <Card withBorder>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={4}>Card on File</Title>
          <Text size="sm" c="gray.6" mt="xs">
            Non-sensitive card details for billing and autopay
          </Text>
        </div>
      </Group>
      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
          <Text size="sm" c="gray.6">Loading payment methods…</Text>
        </Group>
      ) : !defaultCard ? (
        <Text size="sm" c="gray.6">No card on file.</Text>
      ) : (
        <Paper p="md" withBorder>
          <Group justify="space-between" align="flex-start">
            <div>
              <Text size="sm" fw={600}>
                {brandIcon(defaultCard.brand)} •••• {defaultCard.last4}
              </Text>
              <Text size="xs" c="gray.6">
                Expires {String(defaultCard.expMonth).padStart(2, '0')}/{defaultCard.expYear}
              </Text>
              {defaultCard.cardholderName && (
                <Text size="xs" c="gray.6">
                  {defaultCard.cardholderName}
                </Text>
              )}
              <Group gap="xs" mt="sm">
                <Badge variant="light" color="green" size="sm">Active</Badge>
                {defaultCard.isDefault && (
                  <Badge variant="light" color="blue" size="sm">Default</Badge>
                )}
              </Group>
            </div>
          </Group>
        </Paper>
      )}
      {paymentMethods && paymentMethods.length > 1 && (
        <Stack gap="xs" mt="md">
          <Text size="xs" fw={600} c="gray.6">
            {paymentMethods.length - 1} additional payment method
            {paymentMethods.length - 1 > 1 ? 's' : ''} on file
          </Text>
        </Stack>
      )}
    </Card>
  );
}
